import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { FaGithub, FaDiscord, FaTwitter, FaLinkedin, FaArrowUp, FaCode } from 'react-icons/fa';

/****
* Renders the CodeBattles footer with navigation links, social icons, and a scroll-to-top control.
* @example
* Footer()
* <footer className="relative ...">...</footer>
* @param {{}} {{}} - 
* @returns {{JSX.Element}} The footer element for the homepage.
****/
const Footer = () => {
  const location = useLocation();
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Hide scroll-to-top button on About page
  const showScrollButton = location.pathname !== '/about';

  const footerLinks = {
    product: [
      { name: 'Problems', path: '/problems' },
      { name: 'Challenges', path: '/challenges' },
      { name: 'Leaderboard', path: '/leaderboard' },
      { name: 'About', path: '/about' }
    ],
    resources: [
      { name: 'Documentation', path: '/docs' },
      { name: 'API', path: '/api' },
      { name: 'Blog', path: '/blog' },
      { name: 'Support', path: '/support' }
    ],
    company: [
      { name: 'About Us', path: '/about' },
      { name: 'Careers', path: '/careers' },
      { name: 'Privacy', path: '/privacy' },
      { name: 'Terms', path: '/terms' }
    ]
  };

  const socialLinks = [
    { icon: FaGithub, url: 'https://github.com', color: '#fff', label: 'GitHub' },
    { icon: FaDiscord, url: 'https://discord.com', color: '#5865F2', label: 'Discord' },
    { icon: FaTwitter, url: 'https://twitter.com', color: '#1DA1F2', label: 'Twitter' },
    { icon: FaLinkedin, url: 'https://linkedin.com', color: '#0A66C2', label: 'LinkedIn' }
  ];

  return (
    <footer className="relative bg-gradient-to-b from-background-primary to-background-secondary border-t border-border-primary">
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-flex items-center gap-3 mb-4 group">
              <div className="size-12 rounded-xl bg-gradient-to-br from-button-primary-bg to-button-primary-hover flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <FaCode className="text-2xl text-button-primary-text" />
              </div>
              <span className="text-2xl font-extrabold text-text-accent">
                CodeBattles
              </span>
            </Link>
            <p className="text-text-secondary mb-6 max-w-sm leading-relaxed">
              The ultimate platform for competitive coding. Battle, learn, and rise through the ranks with coders worldwide.
            </p>
            {/* Social Links */}
            <div className="flex gap-3">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="size-10 rounded-lg bg-background-hover hover:bg-background-tertiary border border-border-primary hover:border-border-secondary flex items-center justify-center transition-all duration-300 group"
                  aria-label={social.label}
                >
                  <social.icon 
                    className="text-lg text-text-secondary group-hover:text-text-primary transition-colors"
                  />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Links Sections */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h3 className="text-text-primary font-bold mb-4 capitalize">{category}</h3>
              <ul className="space-y-2">
                {links.map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.path}
                      className="text-text-secondary hover:text-text-accent transition-colors duration-200 text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border-primary flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-text-secondary text-sm">
            © {new Date().getFullYear()} CodeBattles. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm text-text-secondary">
            <Link to="/privacy" className="hover:text-text-accent transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-text-accent transition-colors">
              Terms of Service
            </Link>
            <Link to="/cookies" className="hover:text-text-accent transition-colors">
              Cookie Policy
            </Link>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button - Hidden on About page */}
      {showScrollButton && (
        <motion.button
          onClick={scrollToTop}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: false }}
          whileHover={{ scale: 1.1, y: -2 }}
          whileTap={{ scale: 0.95 }}
          className="fixed bottom-8 right-8 size-12 rounded-full bg-gradient-to-br from-button-primary-bg to-button-primary-hover flex items-center justify-center shadow-2xl transition-all duration-300 z-50 group"
          aria-label="Scroll to top"
        >
          <FaArrowUp className="text-button-primary-text text-lg group-hover:translate-y-[-2px] transition-transform" />
        </motion.button>
      )}
    </footer>
  );
};

export default Footer;
